import * as PIXI from 'pixi.js';
import ReactiveMedia from './ReactiveMedia';
import { ASPECT_RATIO, GRADIENT_TYPE, MEDIA_RESOURCE_TYPE } from '../../../CreatePageConstants';

// Longest edge of the generated gradient texture. The texture is produced at the
// export's true aspect ratio so the gradient geometry matches the renderer output.
const GRADIENT_TEXTURE_LONG_EDGE = 1920;

// Frame aspect ratio (width / height) for each supported orientation.
const ASPECT_RATIO_FACTOR = {
    [ASPECT_RATIO.LANDSCAPE]: 16 / 9,
    [ASPECT_RATIO.PORTRAIT]: 9 / 16,
    [ASPECT_RATIO.SQUARE]: 1
};

export default class ReactiveColor extends ReactiveMedia {
    constructor(stage, zIndex) {
        super(stage, zIndex);
        this.gradientTexture = null;
        this.sprite.texture = PIXI.Texture.WHITE;
    }

    update(config) {
        super.update(config);

        if (this.mediaSource === config.mediaSource)
            return;

        this.mediaSource = config.mediaSource;
        const { width, height } = this.resolveTextureDimensions();

        this.releaseGradientTexture();

        if (config.mediaSource?.mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT) {
            const gradientSettings = config.mediaSource?.gradientSettings ?? {};
            this.gradientTexture = this.createGradientTexture(gradientSettings, width, height);
            this.sprite.texture = this.gradientTexture;
            this.sprite.tint = 0xffffff;
        } else {
            const colorSettings = config.mediaSource?.colorSettings ?? {};
            this.sprite.texture = PIXI.Texture.WHITE;
            this.sprite.tint = hexToTint(colorSettings.color);
        }

        // Generating at the export aspect ratio makes the source factor equal the
        // target factor, so correctAspectRatio fills the frame without distortion.
        this.correctAspectRatio(width, height);
        this.refreshAfterSourceDimensionsChange();
    }

    resolveTextureDimensions() {
        const factor = ASPECT_RATIO_FACTOR[this.aspectRatio] ?? ASPECT_RATIO_FACTOR[ASPECT_RATIO.LANDSCAPE];
        if (factor >= 1)
            return { width: GRADIENT_TEXTURE_LONG_EDGE, height: Math.round(GRADIENT_TEXTURE_LONG_EDGE / factor) };
        return { width: Math.round(GRADIENT_TEXTURE_LONG_EDGE * factor), height: GRADIENT_TEXTURE_LONG_EDGE };
    }

    createGradientTexture(gradientSettings, width, height) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        drawGradient(canvas.getContext('2d'), gradientSettings, width, height);
        return PIXI.Texture.from(canvas);
    }

    releaseGradientTexture() {
        if (this.gradientTexture) {
            if (this.sprite && this.sprite.texture === this.gradientTexture) {
                this.sprite.texture = PIXI.Texture.WHITE;
            }
            this.gradientTexture.destroy(true);
            this.gradientTexture = null;
        }
    }

    draw(spectrum) {
        super.draw(spectrum);
    }

    updateStage(stage) {
        super.updateStage(stage);
    }

    destroy() {
        this.releaseGradientTexture();
        super.destroy();
    }
}

// Paints a linear or radial gradient across the whole canvas. Kept identical to
// the renderer's gradient math so preview and exported video stay in sync.
function drawGradient(ctx, gradientSettings, width, height) {
    const colors = gradientSettings.colors ?? [];
    const color = colors[0] ?? '#1e88e5';
    const color2 = colors[1] ?? '#000000';

    let gradient;
    if (isRadialGradient(gradientSettings.type)) {
        const centerX = width / 2;
        const centerY = height / 2;
        const radiusScale = Number(gradientSettings.radius ?? 100) / 100;
        const radius = (Math.max(width, height) / 2) * radiusScale;
        gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    } else {
        // Endpoints run from the canvas center outwards along the angle, so the
        // gradient spans the full frame regardless of orientation.
        const angle = (Number(gradientSettings.angle ?? 90) * Math.PI) / 180;
        const halfWidth = width / 2;
        const halfHeight = height / 2;
        gradient = ctx.createLinearGradient(
            halfWidth - Math.cos(angle) * halfWidth,
            halfHeight - Math.sin(angle) * halfHeight,
            halfWidth + Math.cos(angle) * halfWidth,
            halfHeight + Math.sin(angle) * halfHeight
        );
    }

    gradient.addColorStop(0, color);
    gradient.addColorStop(1, color2);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

// The gradient type may arrive lowercase from the web app state or capitalized
// ("Radial") from the SDK, so the comparison is case-insensitive.
function isRadialGradient(gradientType) {
    return String(gradientType ?? '').toLowerCase() === GRADIENT_TYPE.RADIAL;
}

// Converts a "#rrggbb" string into the numeric tint PixiJS expects.
function hexToTint(colorString) {
    return parseInt((colorString ?? '#ffffff').replace('#', ''), 16);
}
